import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';

import ProtectedRoute from './libs/ProtectedRoute';

import AppLayout from './app/layouts/AppLayout';

// Layouts
import GuestLayout from './views/layouts/GuestLayout/GuestLayout';
import HomeLayout from './views/layouts/HomeLayout/HomeLayout';
import PublicLayout from './views/layouts/PublicLayout/PublicLayout';

// role = Guest
import LoginPage from './views/pages/LoginPage';
import ApiToken from './views/pages/User/ApiToken';
import ApiLog from './views/pages/User/ApiLog';

// App modules
import AppDashboard from './app/modules/dashboard/Dashboard';
import PublicDashboard from './app/modules/dashboard/PublicDashboard';
import AppComplaintList from './app/modules/aduan/ComplaintList';
import AppComplaintListPublic from './app/modules/aduan/ComplaintListPublic';
import AppComplaintListInternal from './app/modules/aduan/ComplaintListInternal';
import AppComplaintDetail from './app/modules/aduan/ComplaintDetail';
import ComplaintCalendar from './app/modules/aduan/ComplaintCalendar';
import ComplaintPrintBorang5 from './app/modules/aduan/ComplaintPrintBorang5';
import ComplaintPrintLaporanTindakan from './app/modules/aduan/ComplaintPrintLaporanTindakan';
import ComplaintPrintTindakanAduan from './app/modules/aduan/ComplaintPrintTindakanAduan';
import ComplaintPrintAkSiasatanSlip from './app/modules/aduan/ComplaintPrintAkSiasatanSlip';
import AppointmentCalendar from './app/modules/appointments/AppointmentCalendar';
import CaseList from './app/modules/cases/CaseList';
import CaseDetail from './app/modules/cases/CaseDetail';
import CasePrintRingkasanKes from './app/modules/cases/CasePrintRingkasanKes';
import StaffList from './app/modules/staff/StaffList';
import RoleList from './app/modules/roles/RoleList';
import MenuList from './app/modules/menus/MenuList';
import UserList from './app/modules/users/UserList';
import AuditTrailList from './app/modules/audit/AuditTrailList';
import EmailDeliveryLog from './app/modules/system/EmailDeliveryLog';
import AduanFlowDashboard from './app/modules/dashboard/AduanFlowDashboard';
import ArahanBeredar from './app/modules/beredar/ArahanBeredar';
import ArahanBeredarList from './app/modules/beredar/ArahanBeredarList';
import ArahanBeredarDetail from './app/modules/beredar/ArahanBeredarDetail';
import ComplaintActionStatusReport from './app/modules/reports/ComplaintActionStatusReport';
import ComplaintJanaTindakanReport from './app/modules/reports/ComplaintJanaTindakanReport';
import ComplaintArrestByDistrictReport from './app/modules/reports/ComplaintArrestByDistrictReport';
import ComplaintStatistikReport from './app/modules/reports/ComplaintStatistikReport';
import WaranStatistikReport from './app/modules/waran/WaranStatistikReport';
import ComplaintAkStatistikReport from './app/modules/reports/ComplaintAkStatistikReport';
import ChangelogView from './app/modules/system/ChangelogView';
import DeployPanel from './app/modules/system/DeployPanel';
import ActivityLogView from './app/modules/system/ActivityLogView';
import ComplaintArrestByOffenseReport from './app/modules/reports/ComplaintArrestByOffenseReport';
import ComplaintArrestorsReport from './app/modules/reports/ComplaintArrestorsReport';
import ComplaintArrestTotalsReport from './app/modules/reports/ComplaintArrestTotalsReport';
import ComplaintCaseProsecutionReport from './app/modules/reports/ComplaintCaseProsecutionReport';
import ComplaintCourtsProsecutorsReport from './app/modules/reports/ComplaintCourtsProsecutorsReport';
import ComplaintIpStatusReport from './app/modules/reports/ComplaintIpStatusReport';
import ComplaintOpNotesReport from './app/modules/reports/ComplaintOpNotesReport';
import ComplaintOydsReport from './app/modules/reports/ComplaintOydsReport';
import ComplaintReport from './app/modules/reports/ComplaintReport';
import ComplaintSeizureItemsReport from './app/modules/reports/ComplaintSeizureItemsReport';
import ReportPlaceholder from './app/modules/reports/ReportPlaceholder';
import WaranFormStepper from './app/modules/waran/WaranFormStepper';
import WaranList from './app/modules/waran/WaranList';
import WaranDetail from './app/modules/waran/WaranDetail';
import WaranReport from './app/modules/waran/WaranReport';
import WaranPrint from './app/modules/waran/WaranPrint';
import WaranOktSearchList from './app/modules/waran/WaranOktSearchList';
import WaranCalendarStatus from './app/modules/waran/WaranCalendarStatus';
import ToastProvider from './app/components/SharedToastProvider';
import SharedDevServerBanner from './app/components/SharedDevServerBanner';
import UserManual from './app/modules/manual/UserManual';
import ScanQr from './app/modules/whatsapp/ScanQr';

// Common
import Profile from './views/pages/Global/Profile';
import AboutUs from './views/pages/Guest/AboutUs';
import SignIn from './views/pages/Guest/SignIn';
import Register from './views/pages/Guest/Register';
import EmailVerified from './views/pages/Guest/EmailVerified';
import ContactUs from './views/pages/Guest/ContactUs';
import { PUBLIC_REGISTRATION_ENABLED } from './config/features';
import SignOut from './views/pages/Guest/SignOut';
import Complaint from './views/pages/Guest/Complaint';
import PublicHomePage from './views/pages/Website/PublicHomePage';
import ComplaintStatus from './views/pages/Website/ComplaintStatus';



import './App.css';
import HomePage from './views/pages/HomePage';

function normalizeRole(value) {
  const role = (value || '').toString().trim().toLowerCase();
  if (!role || role === 'null' || role === 'undefined') {
    return 'awam';
  }
  return role;
}

function DashboardByRole() {
  const role = normalizeRole(localStorage.getItem('role'));
  if (role === 'awam' || role === 'user') {
    return <PublicDashboard />;
  }
  return <AppDashboard />;
}

function ComplaintListByRole() {
  const role = normalizeRole(localStorage.getItem('role'));
  if (role === 'awam' || role === 'user') {
    return <AppComplaintListPublic />;
  }
  return <AppComplaintListInternal />;
}

// The password-protected user manual is a static page in public/docs. Servers that send /docs to the
// SPA (try_files $uri /index.html) land here, so hand over to the static file.
function DocsRedirect() {
  window.location.replace(`/docs/index.html${window.location.hash || ''}`);
  return null;
}

function App() {
  return (
    <Router>
      <ToastProvider>
      <SharedDevServerBanner />
      <Routes>

        <Route path="/" element={<PublicHomePage />} />
        <Route path="/docs" element={<DocsRedirect />} />

        {/* Guest Layout */}
        <Route element={<GuestLayout />}>
          <Route path="/" element={<HomePage/>} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/logout" element={<LoginPage />} />
        </Route>

        {/* Auth Layout */}
        <Route element={<HomeLayout />}>
          <Route path="/sign-in" element={<SignIn />} />
          <Route path="/register" element={PUBLIC_REGISTRATION_ENABLED ? <Register /> : <Navigate to="/sign-in" replace />} />
          <Route path="/email-verified" element={<EmailVerified />} />
        </Route>

        {/* Public Layout */}
        <Route element={<PublicLayout />}>
          <Route path="/about-us" element={<AboutUs />} />
          <Route path="/contact-us" element={<ContactUs />} />
          <Route path="/complaint" element={<Complaint />} />
          <Route path="/semak-status" element={<ComplaintStatus />} />
        </Route>

        {/* User Layout */}
        <Route element={<ProtectedRoute />}>
          <Route path="/app/complaints/:id/print/borang-5" element={<ComplaintPrintBorang5 />} />
          <Route path="/app/complaints/:id/print/tindakan-aduan" element={<ComplaintPrintTindakanAduan />} />
          <Route path="/app/cases/:id/print/laporan-tindakan" element={<ComplaintPrintLaporanTindakan source="case" />} />
          <Route path="/app/cases/:id/print/ringkasan-kes" element={<CasePrintRingkasanKes />} />
          <Route path="/app/complaints/:id/print/ak-siasatan-slip" element={<ComplaintPrintAkSiasatanSlip />} />
          <Route path="/app/i-waran/:id/print" element={<WaranPrint />} />
          <Route path="/app/appointments/popup" element={<AppointmentCalendar isPopup />} />
          <Route element={<AppLayout />}>
            <Route path="/app/dashboard" element={<DashboardByRole />} />
            <Route path="/app/appointments" element={<AppointmentCalendar />} />
            <Route path="/app/cases" element={<CaseList />} />
            <Route path="/app/cases/new" element={<CaseDetail />} />
            <Route path="/app/cases/:id" element={<CaseDetail />} />
            <Route
              path="/app/complaints"
              element={<ComplaintListByRole />}
            />
            <Route path="/app/complaints/calendar" element={<ComplaintCalendar />} />
            <Route
              path="/app/complaints/aj"
              element={<AppComplaintList caseType="AJ" title="Senarai Aduan Jenayah" description="Senarai aduan kategori jenayah (AJ)." />}
            />
            <Route
              path="/app/complaints/ak"
              element={<AppComplaintList caseType="AK" title="Senarai Aduan Keluarga" description="Senarai aduan kategori keluarga (AK)." />}
            />
            <Route
              path="/app/complaints/pending-approval"
              element={<AppComplaintList title="Aduan Untuk Disahkan" description="Senarai aduan yang menunggu pengesahan anda." fetchEndpoint="/complaints/pending-approval" />}
            />
            <Route
              path="/app/complaints/pickup-queue"
              element={<AppComplaintList title="Aduan Untuk Diambil" description="Senarai aduan yang menunggu PIC ambil." fetchEndpoint="/complaints/pickup-queue" enablePickup />}
            />
            <Route
              path="/app/complaints/my-pic"
              element={<AppComplaintList title="Aduan Saya (PIC)" description="Senarai aduan yang telah anda ambil." fetchEndpoint="/complaints/my-pic" />}
            />
            <Route path="/app/complaints/:id" element={<AppComplaintDetail />} />
            <Route path="/app/staff" element={<StaffList />} />
            <Route path="/app/roles" element={<RoleList />} />
            <Route path="/app/changelog" element={<ChangelogView />} />
            <Route path="/app/deploy" element={<DeployPanel />} />
            <Route path="/app/activity-log" element={<ActivityLogView />} />
            <Route path="/app/users" element={<UserList />} />
            <Route path="/app/manual" element={<UserManual />} />
            <Route path="/app/audit-trail" element={<AuditTrailList />} />
            <Route path="/app/email-log" element={<EmailDeliveryLog />} />
            <Route path="/app/aliran-aduan" element={<AduanFlowDashboard />} />
            <Route path="/app/menus" element={<MenuList />} />
            <Route path="/app/api-token" element={<ApiToken />} />
            <Route path="/app/api-logs" element={<ApiLog />} />
            <Route path="/app/scan-qr" element={<ScanQr />} />
            <Route path="/app/arahan-beredar" element={<ArahanBeredarList />} />
            <Route path="/app/arahan-beredar/new" element={<ArahanBeredar />} />
            <Route path="/app/arahan-beredar/:id" element={<ArahanBeredarDetail />} />
            <Route path="/app/arahan-beredar/:id/edit" element={<ArahanBeredar mode="edit" />} />
            <Route path="/app/i-waran" element={<WaranList />} />
            <Route path="/app/i-waran/calendar/status" element={<WaranCalendarStatus />} />
            <Route path="/app/i-waran/semakan-okt" element={<WaranOktSearchList />} />
            <Route path="/app/i-waran/new" element={<WaranFormStepper />} />
            <Route path="/app/i-waran/:id" element={<WaranDetail />} />
            <Route path="/app/i-waran/:id/edit" element={<WaranFormStepper mode="edit" />} />
            <Route
              path="/app/i-waran/report"
              element={<WaranReport />}
            />
            <Route
              path="/app/i-waran/report/statistik"
              element={<WaranStatistikReport />}
            />
            <Route
              path="/app/complaints/report"
              element={<ComplaintReport />}
            />
            <Route
              path="/app/complaints/report/statistik"
              element={<ComplaintStatistikReport />}
            />
            <Route
              path="/app/complaints/report/statistik-ak"
              element={<ComplaintAkStatistikReport />}
            />
            {/* URS 2.0 item 1: Data & Statistik submenus (reuse the combined report via a view filter). */}
            <Route path="/app/data-statistik" element={<Navigate to="/app/data-statistik/aduan" replace />} />
            <Route path="/app/data-statistik/aduan" element={<ComplaintStatistikReport view="aduan" />} />
            <Route path="/app/data-statistik/kes" element={<ComplaintStatistikReport view="kes" />} />
            <Route path="/app/data-statistik/waran" element={<WaranStatistikReport />} />
            <Route path="/app/data-statistik/arahan-beredar" element={<ComplaintStatistikReport view="ab" />} />
            <Route
              path="/app/complaints/report/arrest-by-district"
              element={<ComplaintArrestByDistrictReport />}
            />
            <Route
              path="/app/complaints/report/arrest-by-offense"
              element={<ComplaintArrestByOffenseReport />}
            />
            <Route
              path="/app/complaints/report/arrestors"
              element={<ComplaintArrestorsReport />}
            />
            <Route
              path="/app/complaints/report/arrest-totals"
              element={<ComplaintArrestTotalsReport />}
            />
            <Route
              path="/app/complaints/report/case-prosecution"
              element={<ComplaintCaseProsecutionReport />}
            />
            <Route
              path="/app/complaints/report/oyds"
              element={<ComplaintOydsReport />}
            />
            <Route
              path="/app/complaints/report/seizure-items"
              element={<ComplaintSeizureItemsReport />}
            />
            <Route
              path="/app/complaints/report/courts-prosecutors"
              element={<ComplaintCourtsProsecutorsReport />}
            />
            <Route
              path="/app/complaints/report/ip-status"
              element={<ComplaintIpStatusReport />}
            />
            <Route
              path="/app/jana-tindakan-aduan"
              element={<ComplaintJanaTindakanReport />}
            />
            <Route
              path="/app/complaints/report/jana-tindakan-aduan"
              element={<Navigate to="/app/jana-tindakan-aduan" replace />}
            />
            <Route
              path="/app/complaints/report/action-status"
              element={<ComplaintActionStatusReport />}
            />
            <Route
              path="/app/complaints/report/op/status-semasa"
              element={<ComplaintActionStatusReport
                title="Status Semasa Aduan OP"
                description="Ringkasan status semasa aduan OP mengikut daerah dan tarikh."
                defaultClassification="OP"
                lockClassification
              />}
            />
            <Route
              path="/app/complaints/report/op/catatan"
              element={<ComplaintOpNotesReport />}
            />
            <Route
              path="/app/appointments/report"
              element={<ReportPlaceholder title="Laporan Temujanji" description="Ringkasan temujanji siasatan mengikut tarikh dan pegawai." />}
            />
            <Route
              path="/app/arahan-beredar/report"
              element={<ReportPlaceholder title="Laporan Arahan Beredar" description="Ringkasan rekod arahan beredar untuk pemantauan dalaman." />}
            />
            <Route path="/app/profile" element={<Profile />} />
          </Route>
        </Route>
      </Routes>
      </ToastProvider>
    </Router>
  );
}

export default App;
